import { SoloLitElement } from '@core/solo-lit-element';
import {css, html} from 'lit';
import {GoogleAnalytics} from './google-analytics';
import {FbPixel} from './fb-pixel';
import {BdCookieConsent} from '@bog-design-system/bd-cookie-consent';

export class SoloUserConsent extends SoloLitElement {
    static get is() {
        return 'solo-user-consent';
    }

    static get styles() {
        return css`
            display: contents;
        `;
    }

    static get properties() {
        return {
            data: {
                type: Object,
            },
        };
    }

    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        this.apiCall(`pages/cookie-consent`)
            .then((res) => {
                this.data = res.json.data;
                this.checkUserConsent();
            })
            .catch((err) => {
                console.error('Error fetching cookie consent data:', err);
            });
    }

    static arrayToMap(array) {
        return array.reduce((map, obj) => {
            map.set(obj.channel, obj);
            return map;
        }, new Map());
    }

    renderSlotContent = () => html`
        <bd-cookie-consent-item>
            <bd-font slot="title" type="title">${this.data?.detailsTitle}</bd-font>
            <div slot="description" style="display: flex; flex-direction: column;">
                <bd-font type="body2">${this.data?.detailsDescription} </bd-font>
            </div>
        </bd-cookie-consent-item>

        ${this.data?.toggles?.map(
            (item, index) => html`
                <bd-cookie-consent-item id="${item.id}" ?border=${index === 0}>
                    <bd-font slot="title" type="subtitle">${item?.title}</bd-font>
                    <bd-font slot="description" type="body2"> ${item?.description} </bd-font>
                    <bd-toggle
                        slot="toggle"
                        ?checked="${index === 0}"
                        ?disabled=${index === 0}
                        @checked-changed="${(e) => this.permissionChanged(item?.id, e.detail.value)}"
                    ></bd-toggle>
                </bd-cookie-consent-item>
            `
        )}
    `;

    renderFirstPageDescription = () => html`
        <bd-font type="body2">
            ${this.data?.firstPageDescription}
            <bd-link href="${this.data?.detailsButtonUrl?.link}" target="_blank" type="primary" icon="bd:external"
                >${this.data?.detailsButton}
            </bd-link>
        </bd-font>
    `;

    checkUserConsent() {
        setTimeout(async () => {
            const globalConsent = await this.readGlobalConsent();
            this.channelConsent = this.getLocalConsents(globalConsent);
            this.consents = this.channelConsent.get(SoloUserConsent.domain).consent;
            if (this.isConsentInvalid(this.channelConsent)) {
                BdCookieConsent.open({
                    right: true,
                    acceptAllButtonText: this.data?.acceptAllButtonText || 'Accept all',
                    secondPageTitle: this.data?.secondPageTitle || 'second page title',
                    firstPageTitle: this.data?.firstPageTitle || 'first page title',
                    firstPageDescription: this.renderFirstPageDescription || 'first page description',
                    modify: this.data?.modify || 'modify',
                    save: this.data?.save || 'save',
                    rejectAll: this.data?.rejectAll || 'reject all',
                    renders: this.renderSlotContent,
                })
                    .then(async (newConsent) => {
                        this.setNewConsents(this.channelConsent, globalConsent, newConsent.closeType === 'acceptAll');
                        const consents = this.channelConsent.get(SoloUserConsent.domain).consent;
                        if (consents?.analytics) {
                            GoogleAnalytics.initialize();
                        }
                        if (consents?.advertising) {
                            FbPixel.initialize();
                        }
                    })
                    .catch(() => null);
            } else {
                const consents = this.channelConsent.get(SoloUserConsent.domain)?.consent;
                if (consents?.analytics) {
                    GoogleAnalytics.initialize();
                }
                if (consents?.advertising) {
                    FbPixel.initialize();
                }
            }
        }, 2000);
    }

    getLocalConsents(globalConsent) {
        const localConsentMap = new Map();

        for (const consent of SoloUserConsent.defaultConsent) {
            localConsentMap.set(consent.channel, globalConsent.get(consent.channel) || consent);
        }

        return localConsentMap;
    }

    isConsentInvalid(consents) {
        const validityTime = new Date().getTime() - 1000 * 60 * 60 * 24 * BdCookieConsent.validityDays;
        return Array.from(consents.values()).some((consent) => consent.modified < validityTime);
    }

    async readGlobalConsent() {
        try {
            const globalConsentString = await localStorage.getItem('userConsent');
            const consentRef = JSON.parse(globalConsentString);
            if (Array.isArray(consentRef) === true) {
                return SoloUserConsent.arrayToMap(consentRef);
            }
        } catch (ex) {
            console.log('No user consent found', ex);
        }
        return new Map();
    }

    setNewConsents(channelConsent, globalConsent, acceptAll) {
        const newConsent = new Map([...globalConsent]);

        /* Update modified time for channelConsent entries */
        for (const [key, value] of channelConsent) {
            if (acceptAll) {
                value.consent.analytics = true;
                value.consent.advertising = true;
            }
            value.modified = Date.now();
            newConsent.set(key, value);
        }
        /* Save new consents */
        return localStorage.setItem('userConsent', JSON.stringify(Array.from(newConsent.values())));
    }

    async permissionChanged(channel, value) {
        this.channelConsent.get(SoloUserConsent.domain).consent[channel] = value;
    }

    static get domain() {
        return SoloUserConsent.defaultConsent[0].channel;
    }

    static get defaultConsent() {
        return [
            {
                channel: 'solo.ge',
                modified: 0,
                consent: {
                    essential: true,
                    analytics: false,
                    advertising: false,
                },
            },
        ];
    }
}

window.customElements.define(SoloUserConsent.is, SoloUserConsent);
